import type { TimeEntry } from '../types';
import { parseWorkShifts, expectedMinutesForDate, type WorkShift } from './attendance';

export interface DayData {
  date: string;
  totalMinutes: number;
  expectedMinutes: number;
  normalMinutes: number;
  overtimeMinutes: number;
  entries: TimeEntry[];
}

export interface SummaryStats {
  avgDailyMinutes: number;
  totalMinutes: number;
  totalNormalMinutes: number;
  totalExpectedMinutes: number;
  totalOvertimeMinutes: number;
  daysWithWorkCount: number;
  totalDays: number;
}

export interface BackendWorkSchedule {
  workShifts: unknown;
  workScheduleDays: number[];
  timezone: string;
}

interface UserWorkInfo {
  workShifts: WorkShift[];
  workScheduleDays: number[];
  timezone: string;
}

function getUserWorkInfo(user: TimeEntry['user']): UserWorkInfo {
  return {
    workShifts: parseWorkShifts(user?.workShifts),
    workScheduleDays: user?.workScheduleDays ?? [1, 2, 3, 4, 5],
    timezone: user?.timezone ?? 'UTC',
  };
}

function getNoAdminWorkInfo(backendWorkSchedule?: BackendWorkSchedule | null): UserWorkInfo {
  if (backendWorkSchedule) {
    return {
      workShifts: parseWorkShifts(backendWorkSchedule.workShifts),
      workScheduleDays: backendWorkSchedule.workScheduleDays,
      timezone: backendWorkSchedule.timezone,
    };
  }
  return {
    workShifts: parseWorkShifts(null),
    workScheduleDays: [1, 2, 3, 4, 5],
    timezone: 'UTC',
  };
}

// Helpers to work in UTC so that date boundaries are consistent regardless of
// the runtime timezone (important for server-side rendering and CI).
function isoDatePart(iso: string): string {
  return iso.slice(0, 10);
}

function isSameUTCDay(a: Date, b: Date): boolean {
  return isoDatePart(a.toISOString()) === isoDatePart(b.toISOString());
}

function endOfUTCDay(date: Date): Date {
  return new Date(`${isoDatePart(date.toISOString())}T23:59:59.999Z`);
}

/**
 * Extract a yyyy-MM-dd date key from an ISO timestamp interpreted in a given
 * IANA timezone. Falls back to the UTC date part if the timezone is invalid.
 *
 * This is used to group time entries by the same "day" the user sees in the UI,
 * avoiding mismatches between UTC checkIn dates and browser-local dates.
 */
export function getDateKeyInTimezone(isoString: string, timezone: string): string {
  try {
    const formatter = new Intl.DateTimeFormat('sv-SE', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return formatter.format(new Date(isoString));
  } catch {
    return isoString.slice(0, 10);
  }
}

/**
 * Calculate the duration of a single time entry in minutes.
 *
 * For closed entries the stored duration is used. For active entries
 * (no checkOut) the duration is calculated from checkIn to now, but
 * capped at the end of the checkIn day when the entry is not from today.
 * This prevents absurd overtime in weekly summaries when a user forgets
 * to check out on a previous day.
 */
function calculateEntryDuration(entry: TimeEntry, referenceDate: Date): number {
  if (!entry.checkOut) {
    const checkInDate = new Date(entry.checkIn);
    if (isSameUTCDay(checkInDate, referenceDate)) {
      return Math.max(0, Math.floor((Date.now() - checkInDate.getTime()) / 60000));
    }
    return Math.max(0, Math.floor((endOfUTCDay(checkInDate).getTime() - checkInDate.getTime()) / 60000));
  }
  return entry.durationMinutes ?? 0;
}

/**
 * Group entries by date string (yyyy-MM-dd) in a single pass, using the
 * provided IANA timezone to determine the day for each entry.
 */
export function groupEntriesByDate(
  entries: TimeEntry[],
  timezone: string = 'UTC',
): Map<string, TimeEntry[]> {
  const grouped = new Map<string, TimeEntry[]>();
  for (const entry of entries) {
    // Use the entry's user timezone when available (admin mode includes user
    // data on each entry). This ensures entries are grouped by the correct
    // calendar day for each user, regardless of the admin's browser timezone.
    // Falls back to the provided timezone for non-admin mode (no user data).
    const entryTimezone = entry.user?.timezone ?? timezone;
    const dateKey = getDateKeyInTimezone(entry.checkIn, entryTimezone);
    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }
    grouped.get(dateKey)!.push(entry);
  }
  return grouped;
}

/**
 * Build a single day's summary data from time entries.
 *
 * For non-admin users, the provided backend work schedule is used to compute
 * expected/normal/overtime minutes.
 *
 * For admins, expected/normal/overtime minutes are calculated per user and
 * then summed. This avoids hiding overtime done by one employee just because
 * another employee worked fewer hours the same day (the "all organization"
 * view), and ensures normalMinutes + overtimeMinutes === totalMinutes.
 */
export function buildDayData(
  dateStr: string,
  entries: TimeEntry[],
  isAdmin: boolean,
  backendWorkSchedule?: BackendWorkSchedule | null,
  referenceDate: Date = new Date(),
): DayData {
  let totalMinutes = 0;
  let expectedMinutes = 0;
  let normalMinutes = 0;
  let overtimeMinutes = 0;

  if (isAdmin) {
    const entriesByUser = new Map<string, TimeEntry[]>();
    for (const entry of entries) {
      const userId = entry.user?.id;
      if (!userId) continue;
      if (!entriesByUser.has(userId)) {
        entriesByUser.set(userId, []);
      }
      entriesByUser.get(userId)!.push(entry);
    }

    for (const userEntries of entriesByUser.values()) {
      const workInfo = getUserWorkInfo(userEntries[0].user);
      const userExpected = expectedMinutesForDate(
        dateStr,
        workInfo.workShifts,
        workInfo.workScheduleDays,
        workInfo.timezone,
      );
      const userTotal = userEntries.reduce(
        (sum, entry) => sum + calculateEntryDuration(entry, referenceDate),
        0,
      );
      const userNormal = Math.min(userTotal, userExpected);
      const userOvertime = Math.max(0, userTotal - userExpected);

      totalMinutes += userTotal;
      expectedMinutes += userExpected;
      normalMinutes += userNormal;
      overtimeMinutes += userOvertime;
    }
  } else {
    const workInfo = getNoAdminWorkInfo(backendWorkSchedule);
    expectedMinutes = expectedMinutesForDate(
      dateStr,
      workInfo.workShifts,
      workInfo.workScheduleDays,
      workInfo.timezone,
    );
    totalMinutes = entries.reduce(
      (sum, entry) => sum + calculateEntryDuration(entry, referenceDate),
      0,
    );
    normalMinutes = Math.min(totalMinutes, expectedMinutes);
    overtimeMinutes = Math.max(0, totalMinutes - expectedMinutes);
  }

  return {
    date: dateStr,
    totalMinutes,
    expectedMinutes,
    normalMinutes,
    overtimeMinutes,
    entries,
  };
}

export interface DayUserBreakdown {
  userId: string;
  userName: string;
  totalMinutes: number;
  normalMinutes: number;
  overtimeMinutes: number;
}

/**
 * Build a per-user breakdown of a single day's time entries.
 *
 * In admin mode, entries are grouped by user.id and each user's
 * expected/normal/overtime minutes are computed independently using
 * that user's work schedule. In non-admin mode, all entries belong to
 * the same user and are returned as a single breakdown entry.
 *
 * Returns an empty array when there are no entries.
 */
export function buildDayUserBreakdown(
  dateStr: string,
  entries: TimeEntry[],
  isAdmin: boolean,
  referenceDate: Date = new Date(),
): DayUserBreakdown[] {
  if (entries.length === 0) return [];

  if (!isAdmin) {
    const user = entries[0]?.user;
    const workInfo = user ? getUserWorkInfo(user) : getNoAdminWorkInfo(null);
    const expected = expectedMinutesForDate(
      dateStr,
      workInfo.workShifts,
      workInfo.workScheduleDays,
      workInfo.timezone,
    );
    const total = entries.reduce(
      (sum, entry) => sum + calculateEntryDuration(entry, referenceDate),
      0,
    );
    return [{
      userId: user?.id ?? 'me',
      userName: user ? `${user.firstName} ${user.lastName}`.trim() : '',
      totalMinutes: total,
      normalMinutes: Math.min(total, expected),
      overtimeMinutes: Math.max(0, total - expected),
    }];
  }

  const entriesByUser = new Map<string, TimeEntry[]>();
  for (const entry of entries) {
    const userId = entry.user?.id;
    if (!userId) continue;
    if (!entriesByUser.has(userId)) {
      entriesByUser.set(userId, []);
    }
    entriesByUser.get(userId)!.push(entry);
  }

  const breakdown: DayUserBreakdown[] = [];
  for (const [userId, userEntries] of entriesByUser) {
    const user = userEntries[0].user;
    const workInfo = getUserWorkInfo(user);
    const expected = expectedMinutesForDate(
      dateStr,
      workInfo.workShifts,
      workInfo.workScheduleDays,
      workInfo.timezone,
    );
    const total = userEntries.reduce(
      (sum, entry) => sum + calculateEntryDuration(entry, referenceDate),
      0,
    );
    breakdown.push({
      userId,
      userName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim(),
      totalMinutes: total,
      normalMinutes: Math.min(total, expected),
      overtimeMinutes: Math.max(0, total - expected),
    });
  }

  return breakdown;
}

export function calculateSummaryStats(dayData: DayData[]): SummaryStats {
  const daysWithWork = dayData.filter((d) => d.entries.length > 0 && d.totalMinutes > 0);
  const totalMinutesAll = dayData.reduce((sum, d) => sum + d.totalMinutes, 0);
  const totalNormalMinutes = dayData.reduce((sum, d) => sum + d.normalMinutes, 0);
  const totalExpectedMinutes = dayData.reduce((sum, d) => sum + d.expectedMinutes, 0);
  const totalOvertimeMinutes = dayData.reduce((sum, d) => sum + d.overtimeMinutes, 0);
  const avgDailyMinutes = daysWithWork.length > 0 ? totalMinutesAll / daysWithWork.length : 0;

  return {
    avgDailyMinutes,
    totalMinutes: totalMinutesAll,
    totalNormalMinutes,
    totalExpectedMinutes,
    totalOvertimeMinutes,
    daysWithWorkCount: daysWithWork.length,
    totalDays: dayData.length,
  };
}

export function calculateMaxDayMinutes(dayData: DayData[]): number {
  const max = Math.max(...dayData.map((d) => d.totalMinutes), 0);
  return max > 0 ? max : 1;
}
